import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import {
  completeFunctionalSpecWork,
  approveFunctionalSpec,
  fetchOpenFunctionalSpecTasks,
} from '../../lib/tasks';
import {
  completionStats,
  groupLabelsInOrder,
  isLinkedRequirementsSection,
  rexPlaceholderFor,
  sectionContentByTemplateId,
  sectionsForBuildItemType,
} from '../../lib/functionalSpecSections';
import { downloadFunctionalSpecWord } from '../../lib/functionalSpecWordExport';

const SPEC_STATUS_LABEL = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
};

function specStatusPillClass(status) {
  if (status === 'approved') return 'fse-pill-approved';
  if (status === 'in_review') return 'fse-pill-review';
  return 'fse-pill-draft';
}

export default function FunctionalSpecs() {
  const { project, programme } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const orgId = programme?.organisation_id;
  const [searchParams] = useSearchParams();
  const focusSpecId = searchParams.get('focusSpec');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specs, setSpecs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sectionsBySpec, setSectionsBySpec] = useState(new Map());
  const [linkedReqsByBuildItem, setLinkedReqsByBuildItem] = useState(new Map());
  const [openTasks, setOpenTasks] = useState([]);
  const [activeSpecId, setActiveSpecId] = useState(null);
  const [search, setSearch] = useState('');
  const [rexFilledIds, setRexFilledIds] = useState(new Set());
  const [rexBannerDismissed, setRexBannerDismissed] = useState(new Set());
  const [toast, setToast] = useState(null);
  const saveTimers = useRef(new Map());

  const readOnly = !canWrite;

  const showToast = useCallback((msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    if (!project?.id || !orgId) return;
    setLoading(true);
    try {
      const [specRes, templateRes, taskRows] = await Promise.all([
        sb.from('functional_specs')
          .select(`
            id, status, build_item_id, updated_at,
            deliverables:build_item_id (
              id, ref, title,
              deliverable_types ( name )
            )
          `)
          .eq('project_id', project.id)
          .order('updated_at', { ascending: false }),
        sb.from('functional_spec_section_templates')
          .select('*')
          .eq('organisation_id', orgId)
          .order('order_index'),
        fetchOpenFunctionalSpecTasks(project.id),
      ]);

      const specList = specRes.data || [];
      const specIds = specList.map((s) => s.id);
      const buildItemIds = specList.map((s) => s.build_item_id).filter(Boolean);

      let sectionRows = [];
      if (specIds.length) {
        const { data } = await sb.from('functional_spec_sections')
          .select('*')
          .in('functional_spec_id', specIds);
        sectionRows = data || [];
      }

      let reqLinks = [];
      if (buildItemIds.length) {
        const { data } = await sb.from('requirement_build_items')
          .select('deliverable_id, requirements ( id, ref, title )')
          .in('deliverable_id', buildItemIds);
        reqLinks = data || [];
      }

      const secMap = new Map();
      for (const row of sectionRows) {
        if (!secMap.has(row.functional_spec_id)) secMap.set(row.functional_spec_id, []);
        secMap.get(row.functional_spec_id).push(row);
      }

      const reqMap = new Map();
      for (const link of reqLinks) {
        if (!reqMap.has(link.deliverable_id)) reqMap.set(link.deliverable_id, []);
        reqMap.get(link.deliverable_id).push(link.requirements);
      }

      const rexSet = new Set();
      for (const rows of secMap.values()) {
        for (const r of rows) {
          if (r.rex_filled) rexSet.add(r.section_template_id);
        }
      }

      setSpecs(specList);
      setTemplates(templateRes.data || []);
      setSectionsBySpec(secMap);
      setLinkedReqsByBuildItem(reqMap);
      setOpenTasks(taskRows);
      setRexFilledIds(rexSet);

      const initial = focusSpecId && specList.some((s) => s.id === focusSpecId)
        ? focusSpecId
        : specList[0]?.id;
      setActiveSpecId(initial || null);
    } catch (e) {
      console.error('[FunctionalSpecs] load failed', e);
      showToast('Failed to load functional specs');
    } finally {
      setLoading(false);
    }
  }, [project?.id, orgId, focusSpecId, showToast]);

  useEffect(() => { load(); }, [load]);

  const tasksBySpec = useMemo(() => {
    const map = new Map();
    for (const t of openTasks) {
      if (!map.has(t.source_artefact_id)) map.set(t.source_artefact_id, {});
      map.get(t.source_artefact_id)[t.task_type] = t;
    }
    return map;
  }, [openTasks]);

  const activeSpec = useMemo(
    () => specs.find((s) => s.id === activeSpecId) || null,
    [specs, activeSpecId],
  );

  const activeBuildItem = activeSpec?.deliverables;
  const buildItemType = activeBuildItem?.deliverable_types?.name || '';
  const applicableTemplates = useMemo(
    () => sectionsForBuildItemType(templates, buildItemType),
    [templates, buildItemType],
  );
  const activeSectionRows = useMemo(
    () => sectionsBySpec.get(activeSpecId) || [],
    [sectionsBySpec, activeSpecId],
  );
  const sectionByTemplateId = useMemo(
    () => sectionContentByTemplateId(activeSectionRows),
    [activeSectionRows],
  );
  const linkedReqs = linkedReqsByBuildItem.get(activeSpec?.build_item_id) || [];
  const progress = useMemo(
    () => completionStats(templates, sectionByTemplateId, buildItemType, linkedReqs.length),
    [templates, sectionByTemplateId, buildItemType, linkedReqs.length],
  );

  const filteredSpecs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return specs;
    return specs.filter((s) => {
      const item = s.deliverables;
      return item?.title?.toLowerCase().includes(q)
        || item?.ref?.toLowerCase().includes(q);
    });
  }, [specs, search]);

  function progressForSpec(spec) {
    const type = spec.deliverables?.deliverable_types?.name || '';
    const rows = sectionContentByTemplateId(sectionsBySpec.get(spec.id) || []);
    const reqs = linkedReqsByBuildItem.get(spec.build_item_id) || [];
    return completionStats(templates, rows, type, reqs.length);
  }

  async function ensureSections(specId) {
    const { error } = await sb.rpc('ensure_functional_spec_sections', { p_spec_id: specId });
    if (error) throw error;
    const { data } = await sb.from('functional_spec_sections').select('*').eq('functional_spec_id', specId);
    setSectionsBySpec((prev) => new Map(prev).set(specId, data || []));
    return data || [];
  }

  async function saveSectionContent(sectionRowId, content, rexFilled) {
    const { error } = await sb.from('functional_spec_sections').update({
      content,
      rex_filled: rexFilled,
      updated_at: new Date().toISOString(),
    }).eq('id', sectionRowId);
    if (error) throw error;
  }

  function updateLocalSection(specId, templateId, patch) {
    setSectionsBySpec((prev) => {
      const next = new Map(prev);
      const rows = [...(next.get(specId) || [])];
      const idx = rows.findIndex((r) => r.section_template_id === templateId);
      if (idx >= 0) rows[idx] = { ...rows[idx], ...patch };
      else rows.push({ functional_spec_id: specId, section_template_id: templateId, ...patch });
      next.set(specId, rows);
      return next;
    });
  }

  function handleSectionChange(templateId, value) {
    if (!activeSpecId) return;
    const row = sectionByTemplateId.get(templateId);
    updateLocalSection(activeSpecId, templateId, {
      ...row,
      content: value,
      rex_filled: row?.rex_filled && rexFilledIds.has(templateId),
    });
    if (saveTimers.current.has(templateId)) clearTimeout(saveTimers.current.get(templateId));
    saveTimers.current.set(templateId, setTimeout(async () => {
      try {
        let sectionId = row?.id;
        if (!sectionId) {
          const rows = await ensureSections(activeSpecId);
          sectionId = rows.find((r) => r.section_template_id === templateId)?.id;
        }
        if (sectionId) {
          await saveSectionContent(sectionId, value, rexFilledIds.has(templateId));
        }
      } catch (e) {
        showToast(e.message || 'Failed to save section');
      }
    }, 400));
  }

  function handleSectionBlur(templateId, value) {
    if (rexFilledIds.has(templateId)) {
      setRexFilledIds((prev) => {
        const next = new Set(prev);
        next.delete(templateId);
        return next;
      });
      const row = sectionByTemplateId.get(templateId);
      if (row?.id) {
        saveSectionContent(row.id, value, false).catch(() => {});
      }
      updateLocalSection(activeSpecId, templateId, { content: value, rex_filled: false });
    }
  }

  async function handleRexDraftAll() {
    if (!activeSpecId || readOnly) return;
    setSaving(true);
    try {
      const rows = await ensureSections(activeSpecId);
      const byTemplate = sectionContentByTemplateId(rows);
      const newRex = new Set(rexFilledIds);
      const updates = [];

      for (const t of applicableTemplates) {
        if (isLinkedRequirementsSection(t)) continue;
        const existing = byTemplate.get(t.id);
        if ((existing?.content || '').trim()) continue;
        const content = rexPlaceholderFor(t);
        newRex.add(t.id);
        updates.push({ templateId: t.id, content, rowId: existing?.id });
      }

      for (const u of updates) {
        let rowId = u.rowId;
        if (!rowId) {
          rowId = rows.find((r) => r.section_template_id === u.templateId)?.id;
        }
        if (!rowId) {
          throw new Error('Failed to create section row');
        }
        await saveSectionContent(rowId, u.content, true);
        updateLocalSection(activeSpecId, u.templateId, {
          id: rowId,
          content: u.content,
          rex_filled: true,
        });
      }

      setRexFilledIds(newRex);
      setRexBannerDismissed((prev) => {
        const next = new Set(prev);
        next.delete(activeSpecId);
        return next;
      });
      showToast('Rex drafted the remaining sections — review and adjust below', 'success');
    } catch (e) {
      showToast(e.message || 'Rex draft failed');
    } finally {
      setSaving(false);
    }
  }

  function acceptAllRex() {
    setRexFilledIds(new Set());
    setRexBannerDismissed((prev) => new Set(prev).add(activeSpecId));
    if (activeSpecId) {
      const rows = sectionsBySpec.get(activeSpecId) || [];
      Promise.all(rows.filter((r) => r.rex_filled).map((r) =>
        saveSectionContent(r.id, r.content, false)
      )).then(() => load());
    }
    showToast('Rex draft accepted', 'success');
  }

  async function handleCompleteWork() {
    if (!activeSpec) return;
    const stats = progressForSpec(activeSpec);
    if (stats.done < stats.total) {
      showToast(`Complete all mandatory sections first (${stats.done}/${stats.total})`);
      return;
    }
    const workTask = tasksBySpec.get(activeSpec.id)?.work;
    if (!workTask) return;
    setSaving(true);
    try {
      await completeFunctionalSpecWork(workTask.id);
      showToast('Spec submitted for SA approval', 'success');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to complete work task');
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!activeSpec) return;
    const approvalTask = tasksBySpec.get(activeSpec.id)?.approval;
    if (!approvalTask) return;
    setSaving(true);
    try {
      await approveFunctionalSpec(approvalTask.id);
      showToast('Functional spec approved', 'success');
      await load();
    } catch (e) {
      showToast(e.message || 'Failed to approve spec');
    } finally {
      setSaving(false);
    }
  }

  function handleExportWord() {
    if (!activeSpec || !activeBuildItem) return;
    const filename = downloadFunctionalSpecWord({
      buildItemRef: activeBuildItem.ref,
      buildItemTitle: activeBuildItem.title,
      buildItemType,
      status: activeSpec.status,
      linkedRequirements: linkedReqs,
      templates,
      sectionByTemplateId,
    });
    showToast(`Exported ${filename}`, 'success');
  }

  if (loading) return <Loader text="Loading functional specs…" />;

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 100;
  const rexPendingCount = applicableTemplates.filter((t) => rexFilledIds.has(t.id)).length;
  const showRexBanner = rexPendingCount > 0 && !rexBannerDismissed.has(activeSpecId);
  const groups = groupLabelsInOrder(applicableTemplates);
  const workTask = activeSpec ? tasksBySpec.get(activeSpec.id)?.work : null;
  const approvalTask = activeSpec ? tasksBySpec.get(activeSpec.id)?.approval : null;

  return (
    <div className="fse-shell">
      <div className="fse-left">
        <div className="fse-left-head">
          <div className="fse-eyebrow">Workbench · Design layer</div>
          <div className="fse-title">Functional Specifications</div>
          <input
            className="fse-search"
            placeholder="Search build items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="fse-list">
          {filteredSpecs.map((spec) => {
            const item = spec.deliverables;
            const stats = progressForSpec(spec);
            const rowPct = stats.total ? Math.round((stats.done / stats.total) * 100) : 100;
            return (
              <button
                key={spec.id}
                type="button"
                className={`fse-bi-row${spec.id === activeSpecId ? ' active' : ''}${focusSpecId === spec.id ? ' task-focus' : ''}`}
                onClick={() => setActiveSpecId(spec.id)}
              >
                <div className="fse-bi-row-top">
                  <span className="fse-bi-name">{item?.title || '—'}</span>
                  <span className={`fse-pill ${specStatusPillClass(spec.status)}`}>
                    {SPEC_STATUS_LABEL[spec.status]}
                  </span>
                </div>
                <div className="fse-bi-meta">
                  <span className="fse-bi-ref">{item?.ref || '—'}</span>
                  {' · '}
                  {item?.deliverable_types?.name || '—'}
                </div>
                <div className="fse-progress-mini">
                  <div
                    className={`fse-progress-mini-fill${rowPct < 100 ? ' amber' : ''}`}
                    style={{ width: `${rowPct}%` }}
                  />
                </div>
                <div className="fse-progress-label">
                  {stats.done}/{stats.total} mandatory
                </div>
              </button>
            );
          })}
          {!filteredSpecs.length && (
            <div className="fse-empty-list">
              {specs.length
                ? 'No specs match your search.'
                : 'Confirm disposition on requirements to seed functional specs for linked build items.'}
            </div>
          )}
        </div>
      </div>

      <div className="fse-right">
        {!activeSpec ? (
          <div className="fse-empty-state">Select a build item to edit its functional specification</div>
        ) : (
          <>
            <div className="fse-editor-head">
              <div className="fse-editor-head-top">
                <div>
                  <div className="fse-editor-title-row">
                    <span className="fse-editor-title">{activeBuildItem?.title || '—'}</span>
                    <span className={`fse-pill ${specStatusPillClass(activeSpec.status)}`}>
                      {SPEC_STATUS_LABEL[activeSpec.status]}
                    </span>
                  </div>
                  <div className="fse-editor-sub">
                    {activeBuildItem?.ref || '—'} · {buildItemType || '—'} build item
                  </div>
                </div>
                <div className="fse-head-actions">
                  {!readOnly && activeSpec.status === 'draft' && (
                    <button type="button" className="btn fse-rex-btn" disabled={saving} onClick={handleRexDraftAll}>
                      ✨ Rex: draft remaining sections
                    </button>
                  )}
                  <button type="button" className="btn sm" onClick={handleExportWord}>⬇ Export to Word</button>
                  {!readOnly && activeSpec.status === 'draft' && workTask && (
                    <button type="button" className="btn sm primary" disabled={saving} onClick={handleCompleteWork}>
                      Submit for review
                    </button>
                  )}
                  {!readOnly && activeSpec.status === 'in_review' && approvalTask && (
                    <button type="button" className="btn sm primary" disabled={saving} onClick={handleApprove}>
                      Approve spec
                    </button>
                  )}
                </div>
              </div>
              <div className="fse-progress-strip">
                <div className="fse-progress-bar">
                  <div
                    className={`fse-progress-bar-fill${pct < 100 ? ' amber' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="fse-progress-text">
                  {progress.done}/{progress.total} mandatory sections complete
                </div>
              </div>
            </div>

            {showRexBanner && (
              <div className="fse-rex-banner">
                <span>✨</span>
                <span className="fse-rex-banner-text">
                  Rex drafted <b>{rexPendingCount} section(s)</b> below (shaded purple). Review and adjust — edits clear the draft flag automatically.
                </span>
                <span className="fse-rex-banner-actions">
                  <button type="button" className="btn sm" onClick={() => setRexBannerDismissed((p) => new Set(p).add(activeSpecId))}>Dismiss</button>
                  <button type="button" className="btn sm primary" onClick={acceptAllRex}>Accept all</button>
                </span>
              </div>
            )}

            <div className="fse-editor-scroll">
              {groups.map((group) => {
                const rows = applicableTemplates.filter((t) => t.group_label === group);
                return (
                  <div key={group} className="fse-group-block">
                    <div className="fse-group-title">{group}</div>
                    {rows.map((template) => {
                      if (isLinkedRequirementsSection(template)) {
                        return (
                          <div key={template.id} className="fse-section-card">
                            <div className="fse-section-head">
                              <span className="fse-section-title">{template.name}</span>
                              <span className="fse-required-star">*</span>
                              <span className="fse-sys-tag">System-populated</span>
                            </div>
                            <div className="fse-section-help">{template.help_text}</div>
                            <div className="fse-linked-reqs">
                              {linkedReqs.length
                                ? linkedReqs.map((r) => (
                                  <span key={r.id} className="fse-req-chip">{r.ref || r.title}</span>
                                ))
                                : <span className="fse-muted">No requirements linked via Disposition yet</span>}
                            </div>
                          </div>
                        );
                      }

                      const row = sectionByTemplateId.get(template.id);
                      const content = row?.content || '';
                      const isRex = rexFilledIds.has(template.id);
                      const isEmptyMandatory = template.state === 'mandatory' && !content.trim();
                      const editable = !readOnly && activeSpec.status === 'draft';

                      return (
                        <div key={template.id} className={`fse-section-card${isRex ? ' rex-filled' : ''}`}>
                          <div className="fse-section-head">
                            <span className="fse-section-title">{template.name}</span>
                            {template.state === 'mandatory'
                              ? <span className="fse-required-star">*</span>
                              : <span className="fse-tag-optional">Optional</span>}
                            {isRex && <span className="fse-rex-mini-tag">Rex draft</span>}
                          </div>
                          <div className="fse-section-help">{template.help_text}</div>
                          <textarea
                            className={`fse-section-textarea${isEmptyMandatory ? ' empty-mandatory' : ''}`}
                            value={content}
                            readOnly={!editable}
                            placeholder={template.state === 'mandatory' ? 'Required — not yet completed' : 'Optional'}
                            rows={Math.max(3, Math.min(8, content.split('\n').length + 1))}
                            onChange={(e) => handleSectionChange(template.id, e.target.value)}
                            onBlur={(e) => handleSectionBlur(template.id, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {toast && (
        <div className={`toast show ${toast.type === 'success' ? 'success' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
